import React, {
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { HiArrowRight } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/LandingPageNew/Navbar";
import API from "../utils/api";
import DesktopSidebar from "../components/LandingPageNew/DesktopSidebar";
import Toast from "../components/Shared/Toast";
const STORAGE_KEY = "howRemoveFlow_v1";

function loadFlow() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveFlow(next) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {}
}

function useHowRemoveFlow() {
  const [flow, setFlow] = useState(() => loadFlow());

  const updateFlow = useCallback((patch) => {
    setFlow((prev) => {
      const next = { ...prev, ...patch };
      saveFlow(next);
      return next;
    });
  }, []);

  const resetFlow = useCallback(() => {
    setFlow({});
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, []);

  return { flow, updateFlow, resetFlow };
}

const StepCircle = ({ number, text, active }) => (
  <div className="flex flex-col items-center gap-3 min-w-[90px] text-center">
    <div
      className={`w-[60px] h-[60px] flex items-center justify-center rounded-full text-white text-[24px] font-semibold ${
        active ? "bg-[#0b2b40]" : "bg-[#0b2b40]/30"
      }`}
    >
      {number}
    </div>
    <p
      className={`text-[16px] font-medium ${
        active ? "text-[#0b2b40]" : "text-[#0b2b40]/40"
      }`}
    >
      {text}
    </p>
  </div>
);

const StepLine = ({ active, mobile }) => (
  <div
    className={`rounded-full ${
      mobile ? "w-[35px] h-[4px]" : "w-[130px] h-[4px]"
    } ${
      active
        ? "bg-gradient-to-r from-[#0b2b40] to-white"
        : "bg-gradient-to-r from-[#0b2b40]/30 to-white/30"
    }`}
  />
);

// --- helpers for better UX ---
const FIELD_LABELS = {
  salutation: "Anrede",
  firstName: "Vorname",
  surname: "Nachname",
  email: "E-Mail",
  phone: "Telefon",
  firma: "Firma",
  street: "Straße, Hausnummer",
  plz: "PLZ",
  location: "Ort",
};

// what you want required (tweak as you like)
const REQUIRED_FIELDS = [
  "salutation",
  "firstName",
  "surname",
  "email",
  "phone",
];

const isEmailValid = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());

export default function HowRemove3() {
  const navigate = useNavigate();
  const { flow, updateFlow, resetFlow } = useHowRemoveFlow();
  const [activeTier, setActiveTier] = useState(flow.activeTier ?? 0);
  const business = flow.business || null;
  const reviews = flow.reviews || [];
  const selectedReviews = flow.selectedReviews || [];
  const pricePerDeletion = flow.pricePerDeletion ?? 129.0;
  const [showToast, setShowToast] = useState(false);

  const [formData, setFormData] = useState(
    flow.formData || {
      salutation: "",
      firstName: "",
      surname: "",
      email: "",
      phone: "",
      firma: "",
      street: "",
      plz: "",
      location: "",
    }
  );

  // UX states
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // focus first invalid field
  const fieldRefs = useRef({});

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

 

  useEffect(() => {
    updateFlow({ formData });
  }, [formData, updateFlow]);

  const getReviewText = (review) =>
    review?.snippet ||
    review?.extracted_snippet?.original ||
    "Kein Text verfügbar";

  const getAuthorName = (review) => review?.user?.name || "Unbekannter Nutzer";

  const getFormattedDate = (review) => {
    if (review?.iso_date) {
      return new Date(review.iso_date).toLocaleDateString("de-DE", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
    return review?.date || "Unbekanntes Datum";
  };

  const calculateNewRating = () => {
    if (!business || !reviews) return 0;
    const totalRatingSum =
      (business.rating || 0) * (business.totalReviews || 0);

    const selectedSum = selectedReviews.reduce(
      (acc, idx) => acc + (reviews[idx]?.rating || 0),
      0
    );

    const newTotalReviews =
      (business.totalReviews || 0) - selectedReviews.length;
    if (newTotalReviews <= 0) return 0;

    const newAverage = (totalRatingSum - selectedSum) / newTotalReviews;
    return newAverage.toFixed(1);
  };

  const validate = (data) => {
    const nextErrors = {};

    // required checks
    for (const key of REQUIRED_FIELDS) {
      const value = String(data[key] ?? "").trim();
      if (!value) nextErrors[key] = `${FIELD_LABELS[key]} ist erforderlich.`;
    }

    // email format check (only if provided)
    if (String(data.email || "").trim() && !isEmailValid(data.email)) {
      nextErrors.email = "Bitte geben Sie eine gültige E-Mail-Adresse ein.";
    }

    // phone min length check (optional, adjust)
    const phone = String(data.phone || "").replace(/\s/g, "");
    if (phone && phone.length < 6) {
      nextErrors.phone = "Bitte geben Sie eine gültige Telefonnummer ein.";
    }

    return nextErrors;
  };

  const focusFirstError = (errs) => {
    const firstKey = Object.keys(errs)[0];
    if (!firstKey) return;
    const el = fieldRefs.current[firstKey];
    if (el?.focus) el.focus();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    // clear field error as user types
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const { [name]: _, ...rest } = prev;
      return rest;
    });

    // clear top errors on interaction
    setSubmitError("");
    setSuccessMsg("");
  };

  const parseApiError = (error) => {
    // Try to provide something nicer than a generic alert
    const status = error?.response?.status;
    const apiMessage =
      error?.response?.data?.message || error?.response?.data?.error || "";

    if (status === 400) return apiMessage || "Bitte prüfen Sie Ihre Eingaben.";
    if (status === 429)
      return "Zu viele Anfragen. Bitte versuchen Sie es in wenigen Minuten erneut.";
    if (status >= 500)
      return "Serverfehler. Bitte versuchen Sie es später erneut.";
    return (
      apiMessage || "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut."
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSuccessMsg("");

    const v = validate(formData);
    if (Object.keys(v).length > 0) {
      setErrors(v);
      focusFirstError(v);
      return;
    }

    if (!selectedReviews?.length) {
      setSubmitError(
        "Bitte wählen Sie mindestens eine Bewertung aus, bevor Sie fortfahren."
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const selectedReviewObjects = selectedReviews.map((idx) => {
        const r = reviews[idx];
        return {
          review_id: r?.review_id || r?.id || null,
          authorName: getAuthorName(r),
          date: getFormattedDate(r),
          rating: r?.rating || 0,
          text: getReviewText(r),
        };
      });

      const payload = {
        formName: "Bewertungen – Schritt 3",
        formData,
        business: {
          name: business?.name,
          rating: business?.rating,
          totalReviews: business?.totalReviews,
          place_id: business?.place_id || business?.placeId || business?.id,
        },
        selectedReviews: selectedReviewObjects,
        pricing: {
          pricePerDeletion,
          total: selectedReviewObjects.length * pricePerDeletion,
        },
      };

      await API.post("/contact4", payload);

      setShowToast(true);

      setTimeout(() => {
        resetFlow();
        navigate("/", { replace: true });
      }, 5400);
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitError(parseApiError(error));
    } finally {
      setIsSubmitting(false);
    }
  };
  useEffect(() => {
  if (!business && !showToast) navigate("/");
}, [business, showToast, navigate]);


  const PRICING_TIERS = useMemo(
    () => [
      { min: 0, max: 5, price: 129, nextSave: 200 },
      { min: 6, max: 10, price: 89, nextSave: 50 },
      { min: 11, max: Infinity, price: 79, nextSave: 0 },
    ],
    []
  );

  const selectedCount = selectedReviews.length;
  const currentTierData = PRICING_TIERS[activeTier] || PRICING_TIERS[0];

  const reviewsLeftForNextTier =
    activeTier < PRICING_TIERS.length - 1
      ? PRICING_TIERS[activeTier].max - selectedCount
      : 0;

  const saveAmount = currentTierData.nextSave;

  const getProgressPercentage = () => {
    if (activeTier === 0) return Math.min((selectedCount / 5) * 100, 100);
    if (activeTier === 1) return Math.min(((selectedCount - 5) / 5) * 100, 100);
    return 100;
  };

  // shared input style helpers
  const baseInput =
    "w-full h-[52px] rounded-xl border bg-white px-5 text-base outline-none transition";
  const normalInput =
    "border-black/20 text-black/70 placeholder:text-black/35 focus:border-[#0b2b40] focus:ring-4 focus:ring-[#0b2b40]/10";
  const errorInput =
    "border-red-400 text-black/70 placeholder:text-black/35 focus:border-red-500 focus:ring-4 focus:ring-red-200";

  const FieldError = ({ name }) =>
    errors[name] ? (
      <p className="mt-2 text-sm text-red-600">{errors[name]}</p>
    ) : null;

  return (
    <>
      <Navbar />
      <section className="relative bg-cover bg-no-repeat font-sans py-4 md:py-8 px-4">
        <div className="flex justify-center mt-14">
          <div className="relative w-full max-w-[1300px] mx-auto p-2 lg:p-10 flex flex-col gap-8">
            <div className="flex w-full gap-10">
              <div className="flex flex-col gap-8 transition-all duration-300 w-full md:w-[72%]">
                {/* Header */}
                <div className="flex flex-col gap-3 mb-10 mt-10 items-center text-center">
                  <h2 className="h2-mb h2-md h2-lg h2-2xl">
                    Unfaire Bewertung(en)? <br />
                    <span className="text-[#0b2b40]">
                      Wie Sie die Löschung einleiten:
                    </span>
                  </h2>
                  <p className="text-gray-500 text-sm md:text-lg w-11/12 md:w-11/12 mt-2 text-center">
                    Unser Bewertungsmanager gibt Ihnen die Kontrolle über Ihren
                    Ruf zurück - ganz risikofrei, denn Sie zahlen nur bei
                    erfolgreicher Löschung.
                  </p>
                </div>

                {/* Steps */}
                <div className="hidden lg:flex justify-center mt-2 gap-10 items-center">
                  <StepCircle
                    active={false}
                    number="1"
                    text="Firmenname eingeben"
                  />
                  <StepLine active />
                  <StepCircle
                    active={false}
                    number="2"
                    text="Negative Bewertung auswählen"
                  />
                  <StepLine active />
                  <StepCircle active number="3" text="Vorgang starten" />
                </div>

                <div className="lg:hidden flex justify-center mt-2 gap-6">
                  <StepCircle
                    active={false}
                    number="1"
                    text="Firmenname eingeben"
                  />
                  <StepLine active mobile />
                  <StepCircle
                    active={false}
                    number="2"
                    text="Negative Bewertung auswählen"
                  />
                  <StepLine active mobile />
                  <StepCircle active number="3" text="Vorgang starten" />
                </div>

                {/* STEP 3 FORM */}
                <section className="flex flex-col gap-6 w-full font-san-francisco">
                  <hr className="bg-custom-gray-240 border-0 h-[1px] w-full" />

                  {/* Top status messages */}
                  {(submitError || successMsg) && (
                    <div
                      className={`rounded-2xl border p-4 ${
                        submitError
                          ? "border-red-200 bg-red-50 text-red-800"
                          : "border-green-200 bg-green-50 text-green-800"
                      }`}
                    >
                      <div className="font-semibold mb-1">
                        {submitError
                          ? "Bitte prüfen Sie Ihre Angaben"
                          : "Erfolgreich"}
                      </div>
                      <div className="text-sm">{submitError || successMsg}</div>
                    </div>
                  )}

                  {/* Inline “missing required” summary */}
                  {Object.keys(errors).length > 0 && (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
                      <div className="font-semibold mb-1">Fehlende Angaben</div>
                      <ul className="text-sm list-disc pl-5 space-y-1">
                        {Object.keys(errors).map((k) => (
                          <li key={k}>
                            <button
                              type="button"
                              className="underline underline-offset-2"
                              onClick={() => fieldRefs.current[k]?.focus?.()}
                            >
                              {FIELD_LABELS[k]}
                            </button>{" "}
                            – {errors[k]}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <form
                    id="customer-form"
                    onSubmit={handleSubmit}
                    className="flex flex-col lg:gap-[28px] gap-[14px]"
                    noValidate
                  >
                    {/* Kundeninformationen */}
                    <div className="flex justify-between lg:flex-row flex-col lg:items-center gap-2">
                      <h3 className="lg:text-[24px] text-[20px] lg:leading-[120%] text-black font-semibold">
                        Kundeninformationen
                      </h3>
                      <p className="text-sm text-gray-500">
                        Felder mit <span className="text-red-500">*</span> sind
                        Pflichtfelder.
                      </p>
                    </div>

                    <div className="flex flex-col lg:gap-[10px] gap-[14px]">
                      <div className="flex lg:flex-row flex-col lg:gap-[10px] gap-[14px]">
                        {/* Modern Salutation Select */}
                        <div className="w-full">
                          <label className="text-sm font-medium text-gray-700 mb-2 block">
                            Anrede <span className="text-red-500">*</span>
                          </label>

                          <div className="relative">
                            <select
                              ref={(el) => (fieldRefs.current.salutation = el)}
                              name="salutation"
                              value={formData.salutation}
                              onChange={handleChange}
                              className={`${baseInput} ${
                                errors.salutation ? errorInput : normalInput
                              } pr-12 appearance-none`}
                            >
                              <option value="" disabled>
                                Bitte auswählen…
                              </option>
                              <option value="herr">Herr</option>
                              <option value="frau">Frau</option>
                            </select>

                            {/* custom chevron */}
                            <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-gray-500">
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M6 9L12 15L18 9"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                          </div>

                          <FieldError name="salutation" />
                        </div>

                        {/* First Name */}
                        <div className="w-full">
                          <label className="text-sm font-medium text-gray-700 mb-2 block">
                            Vorname <span className="text-red-500">*</span>
                          </label>
                          <input
                            ref={(el) => (fieldRefs.current.firstName = el)}
                            type="text"
                            name="firstName"
                            placeholder="Vorname"
                            value={formData.firstName}
                            onChange={handleChange}
                            className={`${baseInput} ${
                              errors.firstName ? errorInput : normalInput
                            }`}
                          />
                          <FieldError name="firstName" />
                        </div>

                        {/* Last Name */}
                        <div className="w-full">
                          <label className="text-sm font-medium text-gray-700 mb-2 block">
                            Nachname <span className="text-red-500">*</span>
                          </label>
                          <input
                            ref={(el) => (fieldRefs.current.surname = el)}
                            type="text"
                            name="surname"
                            placeholder="Nachname"
                            value={formData.surname}
                            onChange={handleChange}
                            className={`${baseInput} ${
                              errors.surname ? errorInput : normalInput
                            }`}
                          />
                          <FieldError name="surname" />
                        </div>
                      </div>

                      {/* Email + Phone */}
                      <div className="flex flex-col lg:gap-[10px] gap-[14px]">
                        <div className="w-full">
                          <label className="text-sm font-medium text-gray-700 mb-2 block">
                            E-Mail <span className="text-red-500">*</span>
                          </label>
                          <input
                            ref={(el) => (fieldRefs.current.email = el)}
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="name@firma.de"
                            className={`${baseInput} ${
                              errors.email ? errorInput : normalInput
                            }`}
                          />
                          <FieldError name="email" />
                        </div>

                        <div className="w-full">
                          <label className="text-sm font-medium text-gray-700 mb-2 block">
                            Telefon <span className="text-red-500">*</span>
                          </label>
                          <input
                            ref={(el) => (fieldRefs.current.phone = el)}
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="z.B. +49 151 23456789"
                            className={`${baseInput} ${
                              errors.phone ? errorInput : normalInput
                            }`}
                          />
                          <FieldError name="phone" />
                        </div>
                      </div>
                    </div>

                    {/* Zahlungsdetails */}
                    <div className="flex flex-col lg:gap-[28px] gap-[24px]">
                      <div className="flex justify-between lg:flex-row flex-col lg:items-center gap-2">
                        <h3 className="lg:text-[24px] text-[20px] lg:leading-[120%] text-black font-semibold">
                          Zahlungsdetails
                        </h3>
                        <p className="text-sm text-gray-500">
                          Diese Angaben helfen uns bei der Rechnungsstellung.
                        </p>
                      </div>

                      <div className="flex flex-col lg:gap-[10px] gap-[14px]">
                        <div className="flex lg:flex-row flex-col lg:gap-[10px] gap-[14px]">
                          <div className="w-full">
                            <label className="text-sm font-medium text-gray-700 mb-2 block">
                              Firma
                            </label>
                            <input
                              ref={(el) => (fieldRefs.current.firma = el)}
                              type="text"
                              name="firma"
                              value={formData.firma}
                              onChange={handleChange}
                              placeholder="Firma"
                              className={`${baseInput} ${normalInput}`}
                            />
                          </div>

                          <div className="w-full">
                            <label className="text-sm font-medium text-gray-700 mb-2 block">
                              Straße, Hausnummer
                            </label>
                            <input
                              ref={(el) => (fieldRefs.current.street = el)}
                              type="text"
                              name="street"
                              placeholder="Straße, Hausnummer"
                              value={formData.street}
                              onChange={handleChange}
                              className={`${baseInput} ${normalInput}`}
                            />
                          </div>
                        </div>

                        <div className="flex lg:flex-row flex-col lg:gap-[10px] gap-[14px]">
                          <div className="w-full">
                            <label className="text-sm font-medium text-gray-700 mb-2 block">
                              PLZ
                            </label>
                            <input
                              ref={(el) => (fieldRefs.current.plz = el)}
                              type="text"
                              name="plz"
                              placeholder="PLZ"
                              value={formData.plz}
                              onChange={handleChange}
                              className={`${baseInput} ${normalInput}`}
                            />
                          </div>

                          <div className="w-full">
                            <label className="text-sm font-medium text-gray-700 mb-2 block">
                              Ort
                            </label>
                            <input
                              ref={(el) => (fieldRefs.current.location = el)}
                              type="text"
                              name="location"
                              value={formData.location}
                              onChange={handleChange}
                              placeholder="Ort"
                              className={`${baseInput} ${normalInput}`}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Mobile submit (desktop uses sidebar button) */}
                      <button
                        type="submit"
                        form="customer-form"
                        disabled={isSubmitting}
                        className={`md:hidden w-full py-3 font-semibold rounded-full transition flex gap-8 items-center justify-between px-4 ${
                          isSubmitting
                            ? "bg-[#0b2b40]/60 cursor-not-allowed text-white"
                            : "bg-[#0b2b40] hover:opacity-90 text-white"
                        }`}
                      >
                        {isSubmitting ? "Wird gesendet…" : "Absenden"}
                        <span className="bg-white text-gray-900 p-1 rounded-full flex items-center justify-center">
                          <HiArrowRight className="text-lg transform -rotate-24" />
                        </span>
                      </button>
                    </div>
                  </form>
                </section>
                <div className="text-justify">
                  <div className=" text-xs leading-[133%] text-custom-purple-300 mt-2.5">
                    <span className="text-sm font-bold text-gray-400">
                      Bezahlung nur im Erfolgsfall – Ihre Sicherheit hat für uns
                      höchste Priorität
                    </span>{" "}
                    <br />
                    <span className="text-sm text-gray-400">
                      Vertrauen ist die Grundlage jeder guten Zusammenarbeit.
                      Deshalb haben wir unser Abrechnungsmodell bewusst
                      transparent, fair und vollkommen risikofrei für Sie
                      gestaltet.
                    </span>
                    <div className="font-bold mt-2 mb-2 text-gray-600">
                      So funktioniert unser Erfolgsprinzip:{" "}
                    </div>
                    <div className="pl-4 space-y-2">
                      <div className="flex gap-2 mt-1">
                        <div className="font-bold text-gray-600">1.</div>
                        <div>
                          <span className="font-bold text-gray-600">
                            {" "}
                            Beauftragung{" "}
                          </span>
                          <span className="text-sm text-gray-400">
                            Nach Ihrer Beauftragung erfolgt die Meldung der
                            betreffenden Bewertung(en) durch Sie beim jeweiligen
                            Plattformbetreiber.
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2 mt-1">
                        <div className="font-bold text-gray-600">2.</div>
                        <div>
                          <span className="font-bold text-gray-600">
                            Keine Vorauszahlung{" "}
                          </span>
                          <span className="text-sm text-gray-400">
                            Sie gehen keinerlei finanzielles Risiko ein: Es ist
                            keine Vorauszahlung erforderlich.
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2 mt-1">
                        <div className="font-bold text-gray-600">3.</div>
                        <div>
                          <span className="font-bold text-gray-600">
                            Transparente Erfolgskontrolle{" "}
                          </span>
                          <span className="text-sm text-gray-400">
                            Während des gesamten Prozesses halten wir Sie über
                            alle Fortschritte auf dem Laufenden. Sobald der
                            Prozess abgeschlossen ist, erhalten Sie eine
                            übersichtliche Dokumentation der erzielten
                            Ergebnisse.
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2 mt-1">
                        <div className="font-bold text-gray-600">4.</div>
                        <div>
                          <span className="font-bold text-gray-600">
                            Zahlung ausschließlich nach Erfolg{" "}
                          </span>
                          <span className="text-sm text-gray-400">
                            {" "}
                            Eine Abrechnung erfolgt nur für tatsächlich
                            entfernte Bewertungen durch den Plattformbetreiber.
                            Sollten keine Erfolge erzielt werden, entstehen
                            Ihnen selbstverständlich keinerlei Kosten.
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="font-bold text-gray-600 mt-4 mb-1">
                      Haben Sie Fragen?{" "}
                    </p>
                    <span className="text-sm text-gray-400">
                      Bei Fragen oder Informationsbedarf können Sie uns
                      jederzeit kontaktieren.
                    </span>
                  </div>
                </div>
              </div>

              {/* RIGHT FIXED SIDEBAR (Desktop) */}

              <DesktopSidebar
                currentStep={3}
                business={{ ...business, newRating: calculateNewRating() }}
                selectedReviews={selectedReviews}
                pricePerDeletion={pricePerDeletion}
                activeTier={activeTier}
                reviewsLeftForNextTier={reviewsLeftForNextTier}
                saveAmount={saveAmount}
                getProgressPercentage={getProgressPercentage}
                isSubmitting={isSubmitting}
              />
            </div>

            {/* OPTIONAL: mobile sidebar toggle if you want */}
          </div>
        </div>
      </section>
      {showToast && (
        <Toast
          message="Vielen Dank! Ihre Anfrage wurde erfolgreich übermittelt."
          onClose={() => setShowToast(false)}
        />
      )}
    </>
  );
}
